import { useState, type FormEvent } from 'react'
import { Loader2 } from 'lucide-react'
import { ApiError } from '../lib/api'
import { useSession } from '../lib/session'

export function Login() {
  const { signIn } = useSession()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    if (busy) return

    setBusy(true)
    setError(null)
    try {
      await signIn(email, password)
    } catch (caught) {
      setError(messageFor(caught))
      setBusy(false)
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center p-6">
      <div className="pointer-events-none fixed inset-0 -z-10 bg-bg" />
      <div className="pointer-events-none fixed -left-40 -top-40 -z-10 h-[28rem] w-[28rem] rounded-full bg-accent-wash blur-3xl" />
      <div className="pointer-events-none fixed -right-32 bottom-0 -z-10 h-[26rem] w-[26rem] rounded-full bg-accent-wash blur-3xl" />

      <div className="glass-strong w-full max-w-sm rounded-3xl p-8">
        <div className="mb-7 flex flex-col items-center gap-3 text-center">
          <div className="brand-mark h-14 w-14" aria-hidden="true" />
          <div>
            <h1 className="text-xl font-semibold tracking-tight text-ink">Modulus</h1>
            <p className="text-sm text-muted">Sign in to your school dashboard</p>
          </div>
        </div>

        <form className="space-y-4" onSubmit={onSubmit}>
          <label className="block">
            <span className="mb-1.5 block text-sm font-semibold text-ink">Email</span>
            <input
              type="email"
              required
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-2xl border border-hairline bg-surface px-4 py-2.5 text-sm text-ink outline-none transition placeholder:text-faint focus:border-accent focus:bg-surface focus:ring-4 focus:ring-accent"
              placeholder="you@school.edu"
            />
          </label>

          <label className="block">
            <span className="mb-1.5 block text-sm font-semibold text-ink">Password</span>
            <input
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-2xl border border-hairline bg-surface px-4 py-2.5 text-sm text-ink outline-none transition placeholder:text-faint focus:border-accent focus:bg-surface focus:ring-4 focus:ring-accent"
              placeholder="••••••••"
            />
          </label>

          {error && (
            <p className="rounded-2xl bg-danger-wash px-4 py-2.5 text-sm font-medium text-danger">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={busy}
            className="flex w-full items-center justify-center gap-2 rounded-2xl bg-accent-deep px-4 py-2.5 text-sm font-semibold text-white shadow-md transition hover:brightness-105 disabled:opacity-60"
          >
            {busy && <Loader2 size={16} className="animate-spin" />}
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  )
}

function messageFor(caught: unknown): string {
  if (caught instanceof ApiError) {
    // 400/401 from the auth endpoint is a wrong credential; anything else is worth
    // showing verbatim, since those detail strings are written for humans.
    if (caught.status === 400 || caught.status === 401) {
      return 'That email and password combination is not recognised.'
    }
    if (caught.status === 429) return 'Too many attempts. Please wait a moment and try again.'
    // A dead API behind the dev proxy answers 5xx with no usable body. Saying
    // "request failed (500)" sends someone hunting for a bug in the login form.
    if (caught.status >= 500) return 'The server is not responding. Check that the API is running.'
    return caught.message
  }
  return 'Could not reach the server. Check your connection and try again.'
}
